"use client";

import { ArrowUpRight, ChevronLeft, ChevronRight, ExternalLink, Maximize2, Play, Trophy, X } from "lucide-react";
import { useCallback, useEffect, useLayoutEffect, useRef, useState, useSyncExternalStore } from "react";
import { createPortal } from "react-dom";
import { BrandLogo } from "./brand-logo";

export type CaseStudyWorkItem = {
  id: string;
  badge: string;
  title: string;
  dialogTitle?: string;
  gallery?: CaseStudyGalleryImage[];
  galleryHeading?: string | null;
  openInDialog?: boolean;
  externalLabel?: string;
  externalUrl?: string;
  documentEmbedUrl?: string;
  copyPlacement?: "right";
  pathSlug?: string;
  posterOffset?: {
    x?: number | string;
    y?: number | string;
  };
  posterScale?: number;
  posterSizes?: string;
  posterSrcSet?: string;
  posterUrl?: string;
  preserveTitleLines?: boolean;
  requiresDesktopChrome?: boolean;
  subtitle?: string;
  titleScale?: "compact";
  stream?: CaseStudyStream;
  streams?: CaseStudyStream[];
};

export type CaseStudyStream = {
  aspectRatio?: string;
  embedUrl: string;
  initialMuted?: boolean;
  initialVolume?: number;
  label?: string;
  posterUrl?: string;
  selectorAwards?: string[];
  selectorDetail?: string;
};

export type CaseStudyGalleryImage = {
  src: string;
  alt: string;
};

type CaseStudyPhotoGalleryProps = {
  heading?: string | null;
  images: CaseStudyGalleryImage[];
  label: string;
  variant?: "default" | "selector" | "sidecar";
};

const GALLERY_AUTO_ADVANCE_MS = 4_000;
const GALLERY_CROSSFADE_MS = 440;

/**
 * Reusable expanded-dialog gallery. Supply an image array on any
 * CaseStudyWorkItem to get a selected image, thumbnails, and navigation.
 */
export function CaseStudyPhotoGallery({ heading = null, images, label, variant = "default" }: CaseStudyPhotoGalleryProps) {
  const lightboxRef = useRef<HTMLDialogElement>(null);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const selectedIndexRef = useRef(0);
  const [previousImage, setPreviousImage] = useState<CaseStudyGalleryImage | null>(null);
  const [hasUserInteracted, setHasUserInteracted] = useState(false);
  const isMounted = useSyncExternalStore(() => () => undefined, () => true, () => false);
  const [isPointerOverGallery, setIsPointerOverGallery] = useState(false);
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);

  useEffect(() => {
    const mediaQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
    const updatePreference = () => setPrefersReducedMotion(mediaQuery.matches);

    updatePreference();
    mediaQuery.addEventListener("change", updatePreference);
    return () => mediaQuery.removeEventListener("change", updatePreference);
  }, []);

  const selectGalleryIndex = useCallback((nextIndex: number) => {
    const currentIndex = selectedIndexRef.current;
    const normalizedIndex = (nextIndex + images.length) % images.length;

    if (normalizedIndex === currentIndex) return;

    setPreviousImage(images[currentIndex] ?? null);
    selectedIndexRef.current = normalizedIndex;
    setSelectedIndex(normalizedIndex);
  }, [images]);

  const advanceGallery = useCallback(() => {
    selectGalleryIndex(selectedIndexRef.current + 1);
  }, [selectGalleryIndex]);

  useEffect(() => {
    if (images.length < 2 || hasUserInteracted || isPointerOverGallery || prefersReducedMotion) return;

    const timer = window.setInterval(() => {
      if (document.visibilityState === "visible") {
        advanceGallery();
      }
    }, GALLERY_AUTO_ADVANCE_MS);

    return () => window.clearInterval(timer);
  }, [advanceGallery, hasUserInteracted, images.length, isPointerOverGallery, prefersReducedMotion]);

  useEffect(() => {
    if (!previousImage) return;

    const timeout = window.setTimeout(() => setPreviousImage(null), GALLERY_CROSSFADE_MS);
    return () => window.clearTimeout(timeout);
  }, [previousImage]);

  if (!images.length) return null;

  const selectedImage = images[selectedIndex] ?? images[0];
  const resolvedVariant = images.length > 1 && variant === "default" ? "selector" : variant;
  const stopAutoAdvance = () => setHasUserInteracted(true);
  const selectPrevious = () => {
    stopAutoAdvance();
    selectGalleryIndex(selectedIndexRef.current - 1);
  };
  const selectNext = () => {
    stopAutoAdvance();
    selectGalleryIndex(selectedIndexRef.current + 1);
  };
  const selectImage = (index: number) => {
    stopAutoAdvance();
    selectGalleryIndex(index);
  };
  const openLightbox = () => {
    stopAutoAdvance();
    lightboxRef.current?.showModal();
  };
  const closeLightbox = () => lightboxRef.current?.close();

  return (
    <section
      aria-label={`${label} photo gallery`}
      className={`case-study-photo-gallery${resolvedVariant === "sidecar" ? " case-study-photo-gallery--sidecar" : ""}${resolvedVariant === "selector" ? " case-study-photo-gallery--selector" : ""}`}
      onBlurCapture={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget)) setIsPointerOverGallery(false);
      }}
      onFocusCapture={stopAutoAdvance}
      onPointerDownCapture={stopAutoAdvance}
      onPointerEnter={() => setIsPointerOverGallery(true)}
      onPointerLeave={() => setIsPointerOverGallery(false)}
    >
      <div className={`case-study-photo-gallery-heading${heading ? "" : " case-study-photo-gallery-heading--count-only"}`}>
        {heading ? <p>{heading}</p> : null}
        <span aria-live={hasUserInteracted ? "polite" : "off"}>{selectedIndex + 1} / {images.length}</span>
      </div>
      <div className="case-study-photo-gallery-stage">
        {images.length > 1 ? <button aria-label="Previous photo" className="case-study-photo-gallery-step case-study-photo-gallery-step--previous" onClick={selectPrevious} type="button">
            <ChevronLeft aria-hidden="true" size={22} />
          </button> : null}
        <button aria-label={`Expand photo ${selectedIndex + 1} full screen`} className="case-study-photo-gallery-main" onClick={openLightbox} type="button">
          {/* eslint-disable-next-line @next/next/no-img-element */}
          {previousImage ? <img alt="" aria-hidden="true" className="case-study-photo-gallery-image is-previous" decoding="async" src={previousImage.src} /> : null}
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img alt={selectedImage.alt} className="case-study-photo-gallery-image is-current" decoding="async" key={selectedImage.src} src={selectedImage.src} />
          <span aria-hidden="true" className="case-study-photo-gallery-expand"><Maximize2 size={18} /></span>
        </button>
        {images.length > 1 ? <button aria-label="Next photo" className="case-study-photo-gallery-step case-study-photo-gallery-step--next" onClick={selectNext} type="button">
            <ChevronRight aria-hidden="true" size={22} />
          </button> : null}
      </div>
      {images.length > 1 ? <div aria-label="Choose a photo" className="case-study-photo-gallery-thumbnails">
        {images.map((image, index) => {
          const isSelected = index === selectedIndex;
          return <button
            aria-current={isSelected ? "true" : undefined}
            aria-label={`View photo ${index + 1}: ${image.alt}`}
            aria-pressed={isSelected}
            className={`case-study-photo-gallery-thumbnail${isSelected ? " is-selected" : ""}`}
            key={image.src}
            onClick={() => selectImage(index)}
            type="button"
          >
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img alt="" decoding="async" loading="lazy" src={image.src} />
          </button>;
        })}
      </div> : null}
      {isMounted ? createPortal(<dialog
        aria-label={`${selectedImage.alt}, full-screen view`}
        className="case-study-photo-lightbox"
        onCancel={(event) => {
          event.preventDefault();
          event.stopPropagation();
          closeLightbox();
        }}
        onClose={(event) => event.stopPropagation()}
        onKeyDown={(event) => {
          if (event.key === "Escape") {
            event.preventDefault();
            event.stopPropagation();
            closeLightbox();
            return;
          }
          if (event.key === "ArrowLeft") selectPrevious();
          if (event.key === "ArrowRight") selectNext();
        }}
        ref={lightboxRef}
      >
        <div className="case-study-photo-lightbox-content">
          <button aria-label="Close full-screen photo" className="case-study-photo-lightbox-close" onClick={closeLightbox} type="button">
            <X aria-hidden="true" size={24} />
          </button>
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img alt={selectedImage.alt} decoding="async" key={selectedImage.src} src={selectedImage.src} />
        </div>
      </dialog>, document.body) : null}
    </section>
  );
}

type StreamPlayer = {
  addEventListener: (type: string, listener: () => void, options?: { once?: boolean }) => void;
  muted: boolean;
  volume: number;
};
type StreamPlayerFactory = (iframe: HTMLIFrameElement) => StreamPlayer;

let streamPlayerApiPromise: Promise<StreamPlayerFactory> | undefined;

function loadStreamPlayerApi() {
  if (window.Stream) return Promise.resolve(window.Stream);
  if (streamPlayerApiPromise) return streamPlayerApiPromise;

  streamPlayerApiPromise = new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.async = true;
    script.src = "https://embed.cloudflarestream.com/embed/sdk.latest.js";
    script.onload = () => window.Stream ? resolve(window.Stream) : reject(new Error("Cloudflare Stream Player API unavailable"));
    script.onerror = () => reject(new Error("Cloudflare Stream Player API failed to load"));
    document.head.appendChild(script);
  });

  return streamPlayerApiPromise;
}

declare global {
  interface Window {
    Stream?: StreamPlayerFactory;
  }
}

const mediaItems: CaseStudyWorkItem[] = [
  {
    id: "eras",
    pathSlug: "Product-Eras",
    badge: "COMMUNITY-LED GROWTH",
    title: "Luma's 3\nProduct Eras",
    subtitle: "Radiance Fields → Text to 3D → Text to Video",
    posterUrl: "/media/cards/luma-product-eras-640.webp",
    stream: {
      embedUrl: "https://customer-1gvwgw8ozpno8dq8.cloudflarestream.com/9b08bdc8a46493edba4785e971e51fb3/iframe?loop=true&autoplay=true&poster=https%3A%2F%2Fcustomer-1gvwgw8ozpno8dq8.cloudflarestream.com%2F9b08bdc8a46493edba4785e971e51fb3%2Fthumbnails%2Fthumbnail.jpg%3Ftime%3D%26height%3D600",
    },
  },
  {
    id: "launch-tutorial-content",
    pathSlug: "Launch-Tutorial-Content",
    badge: "CONTENT PRODUCTION",
    title: "Launch & Tutorial Content",
    subtitle: "Planned and executed much of Luma's first-party content.",
    posterUrl: "/media/cards/luma-launch-tutorials-640.webp",
    stream: {
      embedUrl: "https://customer-1gvwgw8ozpno8dq8.cloudflarestream.com/85368731a500d92db0362ba3ad184071/iframe?preload=true&loop=true&autoplay=true&poster=https%3A%2F%2Fcustomer-1gvwgw8ozpno8dq8.cloudflarestream.com%2F85368731a500d92db0362ba3ad184071%2Fthumbnails%2Fthumbnail.jpg%3Ftime%3D%26height%3D600",
      initialMuted: true,
    },
  },
  {
    id: "influencer-marketing",
    pathSlug: "Influencer-Marketing",
    badge: "CREATOR PROGRAMS",
    title: "Influencer\nMarketing",
    subtitle: "Built and ran every creator program at Luma",
    posterUrl: "/media/cards/luma-influencer-640.webp",
    stream: {
      embedUrl: "https://customer-1gvwgw8ozpno8dq8.cloudflarestream.com/cf4d4c7897f5e92c722a7560460e37a7/iframe?loop=true&autoplay=true&poster=https%3A%2F%2Fcustomer-1gvwgw8ozpno8dq8.cloudflarestream.com%2Fcf4d4c7897f5e92c722a7560460e37a7%2Fthumbnails%2Fthumbnail.jpg%3Ftime%3D%26height%3D600",
    },
  },
  {
    id: "emmy",
    pathSlug: "Emmy-Partnerships",
    badge: "PARTNERSHIPS",
    title: "Emmy Award Winning\nPartnerships",
    subtitle: "Phoenix Suns & The Weather Channel. Both sourced from Discord.",
    preserveTitleLines: true,
    posterUrl: "/media/cards/luma-suns-arena-640.webp",
    streams: [
      {
        label: "Phoenix Suns Arena Entrance Video 23–24",
        posterUrl: "https://customer-1gvwgw8ozpno8dq8.cloudflarestream.com/8a1cd6c37c7d153770979cfc428e43b5/thumbnails/thumbnail.jpg?time=&height=400",
        selectorAwards: ["Technical Achievement", "Sports Promotion"],
        selectorDetail: "Rocky Mountain Southwest Emmy Awards",
        embedUrl: "https://customer-1gvwgw8ozpno8dq8.cloudflarestream.com/8a1cd6c37c7d153770979cfc428e43b5/iframe?loop=true&autoplay=true&poster=https%3A%2F%2Fcustomer-1gvwgw8ozpno8dq8.cloudflarestream.com%2F8a1cd6c37c7d153770979cfc428e43b5%2Fthumbnails%2Fthumbnail.jpg%3Ftime%3D%26height%3D600",
      },
      {
        label: "The Weather Channel · FloodFX",
        posterUrl: "https://customer-1gvwgw8ozpno8dq8.cloudflarestream.com/fd288c92af8b6122a15a79541c0a7659/thumbnails/thumbnail.jpg?time=&height=400",
        selectorAwards: ["Outstanding Technical Excellence"],
        selectorDetail: "45th Annual News & Documentary Emmy Awards",
        embedUrl: "https://customer-1gvwgw8ozpno8dq8.cloudflarestream.com/fd288c92af8b6122a15a79541c0a7659/iframe?loop=true&autoplay=true&poster=https%3A%2F%2Fcustomer-1gvwgw8ozpno8dq8.cloudflarestream.com%2Ffd288c92af8b6122a15a79541c0a7659%2Fthumbnails%2Fthumbnail.jpg%3Ftime%3D%26height%3D600",
      },
    ],
  },
  {
    id: "mcdonalds",
    pathSlug: "Mcdonalds",
    badge: "BRAND PARTNERSHIPS",
    title: "McDonald's x Luma\nLunar New Year",
    subtitle: "Introducing new technology to a Fortune 500 brand team",
    gallery: [
      { src: "/media/luma-mcdonalds/gallery-01.webp", alt: "David Romero on the McDonald's Lunar New Year commercial set" },
      { src: "/media/luma-mcdonalds/gallery-02.webp", alt: "McDonald's Lunar New Year commercial production still" },
      { src: "/media/luma-mcdonalds/gallery-03.webp", alt: "Behind the scenes on the McDonald's Lunar New Year commercial set" },
      { src: "/media/luma-mcdonalds/gallery-04.webp", alt: "McDonald's Lunar New Year commercial production setup" },
      { src: "/media/luma-mcdonalds/gallery-05.webp", alt: "McDonald's Lunar New Year commercial set detail" },
      { src: "/media/luma-mcdonalds/gallery-06.webp", alt: "McDonald's Lunar New Year commercial behind the scenes" },
    ],
    posterUrl: "/media/cards/luma-mcdonalds-640.webp",
    stream: {
      embedUrl: "https://customer-1gvwgw8ozpno8dq8.cloudflarestream.com/266f4b54fc209366177e9732b9f328ef/iframe?muted=true&loop=true&autoplay=true&poster=https%3A%2F%2Fcustomer-1gvwgw8ozpno8dq8.cloudflarestream.com%2F266f4b54fc209366177e9732b9f328ef%2Fthumbnails%2Fthumbnail.jpg%3Ftime%3D%26height%3D600",
      initialMuted: true,
      initialVolume: 0.6,
    },
  },
  {
    id: "support-operations",
    pathSlug: "Support-Operations",
    badge: "SUPPORT OPERATIONS",
    title: "3,000 Tickets a Day",
    subtitle: "From under 50 a day through the Dream Machine surge. Built on Freshdesk, Discord, and Stripe.",
  },
];

// One complete four-card sequence used to take 45 seconds. Keep the shared reel
// 50% faster while retaining constant-rate motion and interaction/reduced-motion
// pauses. Velocity must be based on the full physical track, not only the small
// overflow range that remains visible on wide desktop viewports.
const AUTO_SCROLL_SEQUENCE_DURATION_MS = 40_000;

function getReelScrollLimit(viewport: HTMLDivElement) {
  return Math.max(0, viewport.scrollWidth - viewport.clientWidth);
}

function getCardStep(viewport: HTMLDivElement, card: HTMLElement) {
  const reel = viewport.querySelector<HTMLElement>(".luma-showcase-reel--loop");
  const gap = reel ? Number.parseFloat(window.getComputedStyle(reel).columnGap) || 0 : 0;
  return card.offsetWidth + gap;
}

function getRightAlignedLoopStart(
  viewport: HTMLDivElement,
  card: HTMLElement,
  itemCount: number,
) {
  const sequenceWidth = getCardStep(viewport, card) * itemCount;

  // The duplicated first card begins the second seamless sequence. Align its
  // right edge with the viewport so slide one enters fully from the right and
  // remains available to click while the automatic reel carries it leftward.
  return Math.max(0, Math.min(
    sequenceWidth,
    sequenceWidth + card.offsetWidth - viewport.clientWidth,
  ));
}

function isDesktopChrome() {
  const userAgent = window.navigator.userAgent;
  const isChrome = /Chrome\/\d+/.test(userAgent) && !/(Edg|OPR|CriOS)\//.test(userAgent);
  const isMobile = /(Android|iPhone|iPad|iPod|Mobile)/i.test(userAgent);

  return isChrome && !isMobile;
}

function normalizeCaseStudyPath(pathname: string) {
  return decodeURIComponent(pathname).replace(/\/+$/, "").toLowerCase();
}

function getCaseStudyPath(routeBase: string, item: CaseStudyWorkItem) {
  return `/${routeBase}/${item.pathSlug ?? item.id}`;
}

type CaseStudyWorkReelProps = {
  autoScroll?: boolean;
  dialogIdentity?: { brand: "luma" | "lightwheel" | "varjo" | "vive"; companyName: string; title?: string; titleDetail?: string };
  interactiveCards?: boolean;
  items: CaseStudyWorkItem[];
  name: string;
  routeBase?: string;
  sectionHash?: string;
  showPreviousControl?: boolean;
  showControls?: boolean;
  startFlushLeft?: boolean;
};

export function CaseStudyWorkReel({
  autoScroll = false,
  dialogIdentity,
  interactiveCards = false,
  items,
  name,
  routeBase = name.replaceAll(" ", "-"),
  sectionHash = `#${routeBase.toLowerCase().replaceAll("-", "")}`,
  showPreviousControl = true,
  showControls = true,
  startFlushLeft = false,
}: CaseStudyWorkReelProps) {
  // Preserve Luma's established dialog ID while giving every other reusable
  // reel its own deterministic, accessible label target.
  const dialogTitleId = name === "Luma"
    ? "luma-video-dialog-title"
    : `${name.toLowerCase().replace(/\s+/g, "-")}-work-dialog-title`;
  const dialogRef = useRef<HTMLDialogElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const returnFocusRef = useRef<HTMLElement | null>(null);
  const reelViewportRef = useRef<HTMLDivElement>(null);
  const streamFrameRef = useRef<HTMLIFrameElement>(null);
  const interactionTimeoutRef = useRef<number | null>(null);
  const [activeItem, setActiveItem] = useState<CaseStudyWorkItem | null>(null);
  const [activeStreamIndex, setActiveStreamIndex] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const [isInteracting, setIsInteracting] = useState(false);
  const [isInitialScrollReady, setIsInitialScrollReady] = useState(!startFlushLeft);
  const [isRailInViewport, setIsRailInViewport] = useState(false);
  const [isDocumentVisible, setIsDocumentVisible] = useState(false);
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);

  const isAutoScrolling = autoScroll
    && isInitialScrollReady
    && isRailInViewport
    && isDocumentVisible
    && !isHovered
    && !isInteracting
    && !prefersReducedMotion;
  const shouldShowControls = showControls && items.length > 3;

  const activeStreams = activeItem?.streams?.length
    ? activeItem.streams
    : activeItem?.stream
      ? [activeItem.stream]
      : [];
  const activeStream = activeStreams[activeStreamIndex] ?? activeStreams[0];
  const shouldShowGallerySidecar = Boolean(activeStream && activeStreams.length === 1 && activeItem?.gallery?.length);

  useLayoutEffect(() => {
    const viewport = reelViewportRef.current;
    const firstCard = viewport?.querySelector<HTMLElement>(".luma-showcase-card");
    if (!viewport || !firstCard) return;

    const initialScrollLeft = autoScroll && !startFlushLeft
      ? getRightAlignedLoopStart(viewport, firstCard, items.length)
      : 0;
    viewport.scrollLeft = initialScrollLeft;

    if (!startFlushLeft) return;

    // Browser scroll restoration can run after layout and reintroduce the
    // final-card/first-card seam. Reassert Luma's flush-left start after the
    // first painted frame, before its automatic rail movement becomes visible.
    let secondFrame = 0;
    const firstFrame = window.requestAnimationFrame(() => {
      secondFrame = window.requestAnimationFrame(() => {
        viewport.scrollLeft = 0;
      });
    });
    const restorationGuard = window.setTimeout(() => {
      viewport.scrollLeft = 0;
      setIsInitialScrollReady(true);
    }, 1200);

    return () => {
      window.cancelAnimationFrame(firstFrame);
      if (secondFrame) window.cancelAnimationFrame(secondFrame);
      window.clearTimeout(restorationGuard);
    };
  }, [autoScroll, items.length, startFlushLeft]);

  useEffect(() => () => {
    if (interactionTimeoutRef.current !== null) window.clearTimeout(interactionTimeoutRef.current);
  }, []);

  useEffect(() => {
    const mediaQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
    const updatePreference = () => setPrefersReducedMotion(mediaQuery.matches);

    updatePreference();
    mediaQuery.addEventListener("change", updatePreference);
    return () => mediaQuery.removeEventListener("change", updatePreference);
  }, []);

  useEffect(() => {
    const viewport = reelViewportRef.current;
    if (!viewport) return;

    // Start only when the actual card rail is meaningfully on screen; sections
    // above and below the viewport never retain an animation frame loop.
    const observer = new IntersectionObserver(
      ([entry]) => setIsRailInViewport(entry.isIntersecting && entry.intersectionRatio >= 0.15),
      { threshold: [0, 0.15] },
    );

    observer.observe(viewport);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const updateDocumentVisibility = () => setIsDocumentVisible(document.visibilityState === "visible");

    updateDocumentVisibility();
    document.addEventListener("visibilitychange", updateDocumentVisibility);
    return () => document.removeEventListener("visibilitychange", updateDocumentVisibility);
  }, []);

  useEffect(() => {
    const syncItemFromPath = () => {
      const currentPath = normalizeCaseStudyPath(window.location.pathname);
      const linkedItem = items.find((item) => (
        normalizeCaseStudyPath(getCaseStudyPath(routeBase, item)) === currentPath
      ));

      setActiveStreamIndex(0);
      setActiveItem(linkedItem ?? null);
    };

    syncItemFromPath();
    window.addEventListener("popstate", syncItemFromPath);
    return () => window.removeEventListener("popstate", syncItemFromPath);
  }, [items, routeBase]);

  useEffect(() => {
    const viewport = reelViewportRef.current;
    if (!viewport || !isAutoScrolling) return;

    let animationFrame = 0;
    let previousTime = performance.now();
    const firstCard = viewport.querySelector<HTMLElement>(".luma-showcase-card");
    if (!firstCard) return;

    let sequenceWidth = getCardStep(viewport, firstCard) * items.length;
    let pixelsPerMillisecond = sequenceWidth / AUTO_SCROLL_SEQUENCE_DURATION_MS;
    const resizeObserver = new ResizeObserver(() => {
      sequenceWidth = getCardStep(viewport, firstCard) * items.length;
      pixelsPerMillisecond = sequenceWidth / AUTO_SCROLL_SEQUENCE_DURATION_MS;
    });
    resizeObserver.observe(viewport);

    const tick = (now: number) => {
      if (sequenceWidth > 0) {
        const elapsed = now - previousTime;
        previousTime = now;

        const nextPosition = viewport.scrollLeft + pixelsPerMillisecond * elapsed;
        viewport.scrollLeft = nextPosition >= sequenceWidth
          ? nextPosition - sequenceWidth
          : nextPosition;
      }

      animationFrame = window.requestAnimationFrame(tick);
    };

    animationFrame = window.requestAnimationFrame(tick);
    return () => {
      window.cancelAnimationFrame(animationFrame);
      resizeObserver.disconnect();
    };
  }, [isAutoScrolling, items.length]);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog || !activeItem) return;

    dialog.showModal();
    // Native dialog semantics keep the reel inert. Put focus on the visible
    // close control instead of the embedded player so keyboard users receive
    // an immediate, reliable exit affordance.
    window.requestAnimationFrame(() => closeButtonRef.current?.focus());
    return () => {
      if (dialog.open) dialog.close();
    };
  }, [activeItem]);

  const openDialog = (item: CaseStudyWorkItem) => {
    const activeElement = document.activeElement;
    returnFocusRef.current = activeElement instanceof HTMLElement && activeElement !== document.body
      ? activeElement
      : null;
    const itemPath = getCaseStudyPath(routeBase, item);
    if (normalizeCaseStudyPath(window.location.pathname) !== normalizeCaseStudyPath(itemPath)) {
      window.history.pushState({ caseStudy: item.id }, "", itemPath);
    }
    setActiveStreamIndex(0);
    setActiveItem(item);
  };

  const closeDialog = () => {
    dialogRef.current?.close();
    setActiveItem(null);
    if (activeItem) {
      const itemPath = getCaseStudyPath(routeBase, activeItem);
      if (normalizeCaseStudyPath(window.location.pathname) === normalizeCaseStudyPath(itemPath)) {
        window.history.replaceState(null, "", `/${sectionHash}`);
      }
    }
    window.requestAnimationFrame(() => {
      returnFocusRef.current?.focus();
      returnFocusRef.current = null;
    });
  };

  const applyInitialAudio = () => {
    const stream = activeStream;
    const iframe = streamFrameRef.current;
    if (!stream || !iframe) return;

    void loadStreamPlayerApi()
      .then((Stream) => {
        // Stream exposes HTMLMediaElement-like audio properties. Apply once as
        // soon as the API is available and once more when metadata is ready so
        // startup volume is not lost during the iframe/player initialization.
        const player = Stream(iframe);
        const configureAudio = () => {
          if (stream.initialVolume !== undefined) player.volume = stream.initialVolume;
          if (stream.initialMuted !== undefined) player.muted = stream.initialMuted;
        };
        configureAudio();
        player.addEventListener("loadedmetadata", configureAudio, { once: true });
      })
      // The embedded player remains usable if its optional control API is unavailable.
      .catch(() => undefined);
  };

  const pauseForInteraction = () => {
    setIsInteracting(true);
    if (interactionTimeoutRef.current !== null) window.clearTimeout(interactionTimeoutRef.current);
    interactionTimeoutRef.current = window.setTimeout(() => {
      interactionTimeoutRef.current = null;
      setIsInteracting(false);
    }, 1400);
  };

  const moveReel = (direction: -1 | 1) => {
    const viewport = reelViewportRef.current;
    const firstCard = viewport?.querySelector<HTMLElement>(".luma-showcase-card");
    if (!viewport || !firstCard) return;

    pauseForInteraction();

    const cardStep = getCardStep(viewport, firstCard);
    const scrollLimit = getReelScrollLimit(viewport);
    if (scrollLimit <= 0) return;

    if (autoScroll) {
      const sequenceWidth = cardStep * items.length;
      const nextPosition = viewport.scrollLeft + cardStep * direction;
      viewport.scrollLeft = nextPosition >= sequenceWidth
        ? nextPosition - sequenceWidth
        : nextPosition < 0
          ? nextPosition + sequenceWidth
          : nextPosition;
      return;
    }

    // One physical four-card track: reaching the final card returns to the
    // first instead of rendering a second track beside it.
    if (direction === 1) {
      viewport.scrollLeft = viewport.scrollLeft >= scrollLimit - 1
        ? 0
        : Math.min(viewport.scrollLeft + cardStep, scrollLimit);
    } else {
      viewport.scrollLeft = viewport.scrollLeft <= 1
        ? scrollLimit
        : Math.max(0, viewport.scrollLeft - cardStep);
    }
  };

  const renderCard = (item: CaseStudyWorkItem, isDuplicate = false) => {
    const hasVideo = Boolean(item.stream || item.streams?.length);
    const hasExternalLink = Boolean(item.externalUrl);
    const actionVerb = hasVideo ? "Watch" : hasExternalLink ? "Open" : "Read";
    const cardClassName = `luma-showcase-card${hasVideo ? " is-video" : ""}${hasExternalLink ? " is-link" : ""}${item.copyPlacement ? ` luma-showcase-card--copy-${item.copyPlacement}` : ""}`;
    const cardContent = (
      <>
        {item.posterUrl ? (
          <>
            {/* Card artwork is separate from the full Cloudflare Stream player poster. */}
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img
              alt=""
              className="luma-showcase-poster"
              decoding="async"
              loading="lazy"
              sizes={item.posterSizes ?? "(max-width: 720px) 324px, 36vw"}
              src={item.posterUrl}
              srcSet={item.posterSrcSet}
              style={item.posterScale || item.posterOffset ? {
                transform: `translate3d(${typeof item.posterOffset?.x === "number" ? `${item.posterOffset.x}px` : item.posterOffset?.x ?? "0px"}, ${typeof item.posterOffset?.y === "number" ? `${item.posterOffset.y}px` : item.posterOffset?.y ?? "0px"}, 0) scale(${item.posterScale ?? 1})`,
                transformOrigin: "top left",
              } : undefined}
            />
          </>
        ) : null}
        <span className="luma-showcase-badge">{item.badge}</span>
        <span className={`luma-showcase-title${item.preserveTitleLines ? " luma-showcase-title--preserve-lines" : ""}${item.titleScale ? ` luma-showcase-title--${item.titleScale}` : ""}`}>{item.title}</span>
        {item.subtitle ? <span className="luma-showcase-subtitle">{item.subtitle}</span> : null}
        <span className="sr-only">. {actionVerb} case study</span>
      </>
    );
    const cardAction = <span className="luma-showcase-action">
      {hasVideo ? <Play aria-hidden="true" size={18} /> : item.requiresDesktopChrome ? <ExternalLink aria-hidden="true" size={18} /> : <ArrowUpRight aria-hidden="true" size={18} />}
    </span>;

    if (item.externalUrl && !item.openInDialog) {
      return <a
        aria-hidden={isDuplicate || undefined}
        className={cardClassName}
        href={item.externalUrl}
        key={isDuplicate ? `${item.id}-loop` : item.id}
        onClick={(event) => {
          if (item.requiresDesktopChrome && !isDesktopChrome()) {
            event.preventDefault();
            openDialog(item);
          }
        }}
        rel="noopener noreferrer"
        tabIndex={isDuplicate ? -1 : undefined}
        target="_blank"
      >
        {cardContent}
        {cardAction}
      </a>;
    }

    return hasVideo || interactiveCards ? (
      <button
        aria-hidden={isDuplicate || undefined}
        className={cardClassName}
        key={isDuplicate ? `${item.id}-loop` : item.id}
        onClick={() => openDialog(item)}
        tabIndex={isDuplicate ? -1 : undefined}
        type="button"
      >
        {cardContent}
        {cardAction}
      </button>
    ) : (
      <article aria-hidden={isDuplicate || undefined} className={cardClassName} key={isDuplicate ? `${item.id}-loop` : item.id}>
        {cardContent}
      </article>
    );
  };

  return (
    <section aria-label={`${name} featured work`} className="luma-gallery luma-gallery--featured">
      <div className="luma-showcase-shell">
        <div
          aria-label={`${name} featured work`}
          className={`luma-showcase-viewport${startFlushLeft ? " luma-showcase-viewport--flush-left" : ""}`}
          onBlurCapture={(event) => {
            if (!event.currentTarget.contains(event.relatedTarget)) setIsHovered(false);
          }}
          onFocusCapture={() => setIsHovered(true)}
          onPointerEnter={() => setIsHovered(true)}
          onPointerLeave={() => setIsHovered(false)}
          onTouchStart={pauseForInteraction}
          onWheel={pauseForInteraction}
          ref={reelViewportRef}
        >
          <div className={`luma-showcase-reel luma-showcase-reel--loop${!autoScroll && items.length <= 3 ? ` luma-showcase-reel--static luma-showcase-reel--static-${items.length}` : ""}`}>
            {items.map((item) => renderCard(item))}
            {autoScroll ? items.map((item) => renderCard(item, true)) : null}
          </div>
        </div>
        {shouldShowControls ? <div className={`luma-showcase-controls${showPreviousControl ? "" : " luma-showcase-controls--next-only"}`} aria-label={`${name} featured work controls`}>
          {showPreviousControl ? <button aria-label={`Previous ${name} work`} onClick={() => moveReel(-1)} type="button">
            <ChevronLeft aria-hidden="true" size={20} />
          </button> : null}
          <button aria-label={`Next ${name} work`} onClick={() => moveReel(1)} type="button">
            <ChevronRight aria-hidden="true" size={20} />
          </button>
        </div> : null}
      </div>

      <dialog
        aria-labelledby={dialogTitleId}
        className="luma-video-dialog"
        onCancel={closeDialog}
        onClose={() => setActiveItem(null)}
        onKeyDown={(event) => {
          if (event.key === "Escape") {
            event.preventDefault();
            closeDialog();
          }
        }}
        ref={dialogRef}
      >
        {activeItem ? (
          <div className="luma-video-dialog-content">
            <div className="luma-video-dialog-bar">
              {dialogIdentity ? <div aria-label={[dialogIdentity.companyName, dialogIdentity.title, dialogIdentity.titleDetail].filter(Boolean).join(", ")} className="company-role-heading luma-video-dialog-company-role">
                <span className={`company-role-heading-logo company-role-heading-logo-${dialogIdentity.brand}`}><BrandLogo brand={dialogIdentity.brand} decoding="async" loading="lazy" normalizedRole /></span>
                {dialogIdentity.title ? <>
                  <span aria-hidden="true" className="company-role-heading-divider" />
                  <span aria-hidden="true" className="company-role-heading-copy">
                    <span className="company-role-heading-title">
                      {dialogIdentity.title}
                      {dialogIdentity.titleDetail ? <span className="company-role-heading-detail">{dialogIdentity.titleDetail}</span> : null}
                    </span>
                  </span>
                </> : null}
              </div> : <p>{name}</p>}
              <button aria-label="Close case study dialog" onClick={closeDialog} ref={closeButtonRef} type="button"><X aria-hidden="true" size={20} /></button>
            </div>
            {activeStream ? <div className={`luma-video-carousel${activeStreams.length > 1 || shouldShowGallerySidecar ? " luma-video-carousel--with-selector" : ""}${shouldShowGallerySidecar ? " luma-video-carousel--with-gallery" : ""}`}>
              <div className="luma-video-carousel-primary">
                <div className="luma-video-carousel-media">
                  <iframe
                    allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture"
                    allowFullScreen
                    className={`luma-stream-player${activeStream.aspectRatio === "1 / 1" ? " luma-stream-player--square" : ""}${activeStream.aspectRatio === "9 / 16" ? " luma-stream-player--portrait" : ""}`}
                    key={activeStream.embedUrl}
                    onLoad={applyInitialAudio}
                    ref={streamFrameRef}
                    src={activeStream.embedUrl}
                    style={{ aspectRatio: activeStream.aspectRatio ?? "16 / 9" }}
                    title={`${activeItem.title.replace("\n", " ")}: ${activeStream.label ?? "video"}`}
                  />
                </div>
                {activeStream.label ? <div aria-live="polite" className="luma-video-carousel-meta">
                  <span>{activeStream.label}</span>
                  <span>{activeStreamIndex + 1} / {activeStreams.length}</span>
                </div> : null}
              </div>
              {activeStreams.length > 1 ? <div aria-label={`Choose a ${activeItem.title.replace("\n", " ")} video`} className="luma-video-selector">
                {activeStreams.map((stream, index) => {
                  const isActive = index === activeStreamIndex;
                  const streamLabel = stream.label ?? `Video ${index + 1}`;

                  return <button
                    aria-label={`Play ${streamLabel}`}
                    aria-pressed={isActive}
                    className={`luma-video-selector-item${isActive ? " is-active" : ""}`}
                    key={stream.embedUrl}
                    onClick={() => setActiveStreamIndex(index)}
                    type="button"
                  >
                    {stream.posterUrl ? <>
                      {/* Cloudflare owns and optimizes these generated Stream thumbnails. */}
                      {/* eslint-disable-next-line @next/next/no-img-element */}
                      <img alt="" decoding="async" loading="lazy" src={stream.posterUrl} />
                    </> : <span aria-hidden="true" className="luma-video-selector-placeholder" />}
                    <span className="luma-video-selector-copy">
                      <strong>{streamLabel}</strong>
                      {stream.selectorAwards?.map((award) => <span className="luma-video-selector-award" key={award}>
                        <Trophy aria-hidden="true" size={12} strokeWidth={2.5} />
                        {award}
                      </span>)}
                      {stream.selectorDetail ? <span className="luma-video-selector-detail">{stream.selectorDetail}</span> : null}
                      <span className="luma-video-selector-status">{isActive ? "Now playing" : "Play video"}</span>
                    </span>
                  </button>;
                })}
              </div> : shouldShowGallerySidecar && activeItem.gallery?.length ? <CaseStudyPhotoGallery
                heading={activeItem.galleryHeading}
                images={activeItem.gallery}
                key={activeItem.id}
                label={activeItem.title.replace("\n", " ")}
                variant="sidecar"
              /> : null}
            </div> : null}
            {activeItem.gallery?.length && !shouldShowGallerySidecar ? <CaseStudyPhotoGallery
              heading={activeItem.galleryHeading}
              images={activeItem.gallery}
              key={activeItem.id}
              label={activeItem.title.replace("\n", " ")}
            /> : null}
            {activeItem.documentEmbedUrl ? <iframe
              className="luma-document-embed"
              loading="lazy"
              sandbox="allow-popups allow-popups-to-escape-sandbox allow-scripts"
              src={activeItem.documentEmbedUrl}
              title={`${activeItem.title.replace("\n", " ")} embedded article`}
            /> : null}
            <h4 id={dialogTitleId}>{activeItem.dialogTitle ?? activeItem.title}</h4>
            {activeItem.requiresDesktopChrome ? <p className="luma-video-dialog-compatibility">
              This interactive Teleport capture needs Desktop Chrome for most features to work. You can still open it here, but the experience may be limited.
            </p> : null}
            {activeItem.externalUrl ? <a
              className="luma-video-dialog-link"
              href={activeItem.externalUrl}
              rel="noopener noreferrer"
              target="_blank"
            >
              {activeItem.externalLabel ?? "View source"}
              <ArrowUpRight aria-hidden="true" size={18} />
            </a> : null}
          </div>
        ) : null}
      </dialog>
    </section>
  );
}

export function LumaMediaGallery() {
  return <CaseStudyWorkReel
    autoScroll
    dialogIdentity={{ brand: "luma", companyName: "Luma AI", title: "Founding Community & Growth Manager" }}
    interactiveCards
    items={mediaItems}
    name="Luma"
    routeBase="Luma"
    sectionHash="#luma"
    showPreviousControl={false}
    startFlushLeft
  />;
}
